import { IconX } from '@developerz/ui';
import { createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { useDialogTrap } from '../lib/dialog-trap';
import { useClosing, useScrollLock } from '../lib/overlay';
import { buttonClass } from './ui/Button';

// BottomSheet primitive: a floating overlay that pins to the bottom on phones
// (full-bleed sheet) and centres as a panel on desktop. Same focus-trap,
// escape-close, focus-restore, scroll lock and unmount delay the other
// overlay shells share, keyed off the one `useDialogTrap` — `useDialogTrap`
// pushes its own modal layer on the shared stack, so a dialog opened OVER a
// sheet takes Escape first (no separate `pushLayer` call here: this is a
// modal trap, and Tab is also its business).
//
// Mounts only the trigger until open, then the sheet, mirroring Popover's
// uncontrolled shape: callers render one `<BottomSheet/>` per trigger site
// and never share an open slot, so two sheets on a page never fight over it.
//
// Render-prop children receive `close` so a control inside the sheet (a Done
// button, a destructive confirm) can dismiss after acting — the same pattern
// `Popover` uses for menu picks.

export type BottomSheetProps = {
  /** Accessible name for the trigger button. */
  readonly label: string;
  /** Trigger button content — icon, text, or both. */
  readonly trigger: JSX.Element;
  /** Extra class on the trigger button. */
  readonly triggerClass?: string;
  /** Title shown in the sheet's head row and announced via aria-label. */
  readonly title: string;
  /**
   * Sheet content, rendered only while open. Receives `close` so a control
   * inside the sheet can dismiss after acting.
   */
  readonly children: (close: () => void) => JSX.Element;
};

export function BottomSheet(props: BottomSheetProps): JSX.Element {
  const [open, setOpen] = createSignal(false);
  let sheet: HTMLDivElement | undefined;
  const close = (): void => {
    setOpen(false);
  };
  useDialogTrap(
    () => open(),
    () => sheet,
    close,
  );
  useScrollLock(() => open());
  const gate = useClosing(() => open(), 220);

  return (
    <>
      <button
        type="button"
        class={`bottom-sheet__trigger ${props.triggerClass ?? ''}`.trim()}
        aria-haspopup="dialog"
        aria-expanded={open()}
        aria-label={props.label}
        onClick={() => setOpen((v) => !v)}
      >
        {props.trigger}
      </button>
      <Show when={gate.visible()}>
        <div class="bottom-sheet" classList={{ 'bottom-sheet--closing': gate.closing() }}>
          <button
            type="button"
            class="bottom-sheet__overlay"
            tabindex="-1"
            aria-label={t('modal.dismiss')}
            onClick={close}
          />
          <div
            class="bottom-sheet__panel"
            role="dialog"
            aria-modal="true"
            aria-label={props.title}
            tabindex="-1"
            ref={sheet}
          >
            <header class="bottom-sheet__header">
              <h2 class="bottom-sheet__title">{props.title}</h2>
              <button
                type="button"
                class={`${buttonClass({ variant: 'ghost', size: 'icon-sm' })} bottom-sheet__close`}
                aria-label={t('modal.close')}
                onClick={close}
              >
                <IconX size="1rem" />
              </button>
            </header>
            <div class="bottom-sheet__body">{props.children(close)}</div>
          </div>
        </div>
      </Show>
    </>
  );
}
